import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import Box from '../../../../components/Box';
import CustomSingleSelect, { CustomSingleSelectModel } from '../../../../components/CustomSingleSelect';
import CustomNumberInput from '../../../../components/CustomNumberInput';
import CustomTextInput from '../../../../components/CustomTextInput';
import { Helpers } from '../../../../helpers/Helpers';
import { ConstantService } from '../../../../services/ConstantService';
import { storeAreYouSureModalCallback } from '../../../../redux/reducers/authSlice';
import { KTCard } from '../../../../_metronic/helpers/components/KTCard';
import { KTCardBody } from '../../../../_metronic/helpers/components/KTCardBody';

/** Maliyet kalemlerinin tutuldugu sabit tanim kategorisi. */
export const COST_ITEM_CATEGORY = 'ESCALATION_COST_ITEM';

export interface EscalationItemRow {
    escalationItemId?: string
    costItemId?: string
    costItemName?: string
    /** Deger (%). Kutuda "40.5" gibi tutulur, tabloda "40,50" gorunur. */
    value?: string
    description?: string
    isSelected: boolean
}

interface Props {
    items: EscalationItemRow[]
    setItems: React.Dispatch<React.SetStateAction<EscalationItemRow[]>>
}

const EscalationItems = ({ items, setItems }: Props) => {
    const dispatch = useDispatch();

    const [isOpen, setIsOpen] = useState(false);
    const [rowId, setRowId] = useState<string | undefined>(undefined);
    const [selectedCostItem, setSelectedCostItem] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [value, setValue] = useState<string | undefined>(undefined);
    const [description, setDescription] = useState<string | undefined>(undefined);

    const secililer = items.filter(x => x.isSelected);

    /** Satirlarin toplami. Ekranda %100 beklenir; degilse uyari verilir ama kayit engellenmez. */
    const toplam = items.reduce((t, x) => t + Helpers.toNumber(x.value), 0);
    const toplamSapiyor = Math.abs(toplam - 100) > 0.001;

    const temizle = () => {
        setRowId(undefined);
        setSelectedCostItem(undefined);
        setValue(undefined);
        setDescription(undefined);
    }

    const formuAcKapa = () => {
        if (isOpen) {
            temizle();
            setIsOpen(false);
            return;
        }

        temizle();
        setIsOpen(true);
    }

    const setUpdateItem = () => {
        var o = secililer[0];

        setRowId(o.escalationItemId);
        setSelectedCostItem(o.costItemId ? { label: o.costItemName + '', value: o.costItemId } : undefined);
        setValue(o.value);
        setDescription(o.description);
        setIsOpen(true);
    }

    const saveItem = () => {
        if (!selectedCostItem) {
            toast.error('Maliyet Kalemi alanını doldurunuz!');
            return;
        }

        if (Helpers.isEmpty(value)) {
            toast.error('Değer (%) alanını doldurunuz!');
            return;
        }

        // Ayni kalem iki satirda olursa listede tek kolona iki deger duser.
        var ayniKalem = items.filter(x => x.costItemId == selectedCostItem.value && x.escalationItemId != rowId);

        if (ayniKalem.length > 0) {
            toast.error('Bu maliyet kalemi zaten eklenmiş. Mevcut satırı güncelleyiniz.');
            return;
        }

        if (Helpers.isEmpty(rowId)) {
            setItems([...items, {
                escalationItemId: Helpers.generateUUID(true),
                costItemId: selectedCostItem.value,
                costItemName: selectedCostItem.label,
                value: value,
                description: description,
                isSelected: false
            }]);
        }
        else {
            setItems(items.map(function (x) {
                if (x.escalationItemId == rowId) {
                    x.isSelected = false;
                    x.costItemId = selectedCostItem.value;
                    x.costItemName = selectedCostItem.label;
                    x.value = value;
                    x.description = description;
                }

                return x;
            }));
        }

        temizle();
        setIsOpen(false);
    }

    const deleteItems = () => {
        setItems(items.filter(x => !x.isSelected));
    }

    const setItemSelected = (_id: string | undefined) => {
        setItems(items.map(function (x) {
            if (x.escalationItemId == _id)
                x.isSelected = !x.isSelected;

            return x;
        }));
    }

    const selectAll = () => {
        var hepsiSecili = items.length > 0 && items.length == secililer.length;

        setItems(items.map(function (x) {
            x.isSelected = !hepsiSecili;
            return x;
        }));
    }

    return (
        <div className='containerPart'>
            <KTCard className="ebbe-card mt-10">
                <KTCardBody>
                    <h4 className="mb-10 text-black">Eskalasyon Dağılımı</h4>

                    {
                        isOpen && (
                            <React.Fragment>
                                <div className="row">
                                    <div className="col-md-4">
                                        <CustomSingleSelect
                                            isLabelRequired
                                            labelText="Maliyet Kalemi"
                                            selectedValue={selectedCostItem}
                                            onChange={(secilen) => {
                                                setSelectedCostItem(secilen);

                                                // Kalemin varsayilan yuzdesi varsa ve kutu bossa oradan gelsin.
                                                if (Helpers.isEmpty(value) && !Helpers.isEmpty(secilen?.additionalValue1))
                                                    setValue(secilen?.additionalValue1);
                                            }}
                                            onMenuOpen={() => ConstantService.getConstantList(COST_ITEM_CATEGORY)}
                                            labelName='value'
                                            valueName='constantId'
                                            additionalValue1Name='additionalValue1'
                                            insertable={{
                                                tooltipText: 'Listede olmayan bir maliyet kalemini buradan ekleyebilirsiniz.',
                                                onSubmit: { code: COST_ITEM_CATEGORY }
                                            }}
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <CustomNumberInput
                                            isLabelRequired
                                            labelText="Değer (%)"
                                            value={value}
                                            setValue={setValue}
                                        />
                                    </div>

                                    <div className="col-md-5">
                                        <CustomTextInput
                                            labelText="Açıklama"
                                            value={description}
                                            setValue={setDescription}
                                        />
                                    </div>
                                </div>

                                <div className="row mt-5">
                                    <div className="col-md-12">
                                        <Box alignItems="flex-end">
                                            <Box onClick={saveItem} className="actionIcon p-2 bg-success" justifyContent="center" flexDirection="row" borderRadius="4px">
                                                <Box mr="5px" justifyContent="center">
                                                    <i className="text-white fa fa-check" />
                                                </Box>
                                                <Box justifyContent="center">
                                                    <span style={{ fontWeight: 600 }} className="text-white">Kaydet</span>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </div>
                                </div>

                                <Box className='ebbe-hr m-0 mt-5'>&nbsp;</Box>
                            </React.Fragment>
                        )
                    }

                    <Box className={isOpen ? "mt-5" : ""} flexDirection="row">
                        <Box onClick={formuAcKapa} className={"actionIcon p-2 " + (!isOpen ? "bg-success" : "")} bg={isOpen ? 'orange' : undefined} justifyContent="center" flexDirection="row" borderRadius="4px">
                            <Box mr="5px" justifyContent="center">
                                <i className={"text-white fa " + (!isOpen ? 'fa-plus' : 'fa-times')} />
                            </Box>
                            <Box justifyContent="center">
                                <span style={{ fontWeight: 600 }} className="text-white">{!isOpen ? 'Ekle' : 'İptal Et'}</span>
                            </Box>
                        </Box>

                        <Box onClick={secililer.length == 1 ? setUpdateItem : undefined} opacity={secililer.length == 1 ? "1" : "0.5"} ml="10px" className="actionIcon p-2 bg-primary" justifyContent="center" flexDirection="row" borderRadius="4px">
                            <Box mr="5px" justifyContent="center">
                                <i className="text-white fa fa-edit" />
                            </Box>
                            <Box justifyContent="center">
                                <span style={{ fontWeight: 600 }} className="text-white">Güncelle</span>
                            </Box>
                        </Box>

                        <Box onClick={secililer.length > 0 ? () => dispatch(storeAreYouSureModalCallback(deleteItems)) : undefined} opacity={secililer.length > 0 ? "1" : "0.5"} ml="10px" className="actionIcon p-2 bg-danger" justifyContent="center" flexDirection="row" borderRadius="4px">
                            <Box mr="5px" justifyContent="center">
                                <i className="text-white fa fa-trash" />
                            </Box>
                            <Box justifyContent="center">
                                <span style={{ fontWeight: 600 }} className="text-white">Sil</span>
                            </Box>
                        </Box>
                    </Box>

                    <div className="row mt-5">
                        <div className="col-md-12">
                            <div className='table-responsive'>
                                <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer'>
                                    <thead>
                                        <tr className='text-start text-muted fw-bolder text-uppercase'>
                                            <th style={{ width: 1 }}>
                                                <div className='d-flex'>
                                                    <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                        <input onChange={selectAll} className='form-check-input' type='checkbox' checked={items.length > 0 && items.length == secililer.length} />
                                                    </label>
                                                </div>
                                            </th>
                                            <th>Maliyet Kalemi</th>
                                            <th className='custom-text-right'>Değer (%)</th>
                                            <th>Açıklama</th>
                                        </tr>
                                    </thead>
                                    <tbody className='text-gray-600 fw-bold'>
                                        {
                                            items.length == 0 && (
                                                <tr>
                                                    <td className="text-center" colSpan={99}>Kayıt bulunamadı.</td>
                                                </tr>
                                            )
                                        }
                                        {
                                            items.map((item, index) =>
                                                <tr key={item.escalationItemId} style={{ background: item.isSelected ? 'rgba(204, 204, 204, 0.25)' : undefined }}>
                                                    <td style={{ width: 1 }}>
                                                        <div className='d-flex'>
                                                            <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                                <input onChange={() => setItemSelected(item.escalationItemId)} className='form-check-input' type='checkbox' checked={item.isSelected} />
                                                            </label>
                                                        </div>
                                                    </td>
                                                    <td onClick={() => setItemSelected(item.escalationItemId)}>{item.costItemName}</td>
                                                    <td className='custom-text-right' onClick={() => setItemSelected(item.escalationItemId)}>{Helpers.correctPrice(item.value, 2)}</td>
                                                    <td onClick={() => setItemSelected(item.escalationItemId)}>{item.description}</td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                    {
                                        items.length > 0 && (
                                            <tfoot>
                                                <tr className='eskalasyon-toplam'>
                                                    <td></td>
                                                    <td>TOPLAM</td>
                                                    <td className={'custom-text-right eskalasyon-toplam-deger ' + (toplamSapiyor ? 'text-danger' : 'text-success')}>{Helpers.correctPrice(toplam, 2)}</td>
                                                    <td className='text-danger'>{toplamSapiyor ? 'Toplam %100 değil — yine de kaydedebilirsiniz' : ''}</td>
                                                </tr>
                                            </tfoot>
                                        )
                                    }
                                </table>
                            </div>
                        </div>
                    </div>
                </KTCardBody>
            </KTCard>
        </div>
    )
}

export default EscalationItems;
